// SB2 (register §35, D-SB3) carried into Arc RM (RM-CAN-05/09): launchability
// from Grants only, menus as folds. The World's board registry answers, per
// row, whether the person may launch it (the entitlement fold rides the door:
// BoardEntitledRow.launchable) and whether the board opens as a ROOM
// (openableAsRoom — a workroom, born through its chip) or as a board in the
// active session-view's deck. No component stores a board list — every
// launcher entry derives from the registry read, and absence of entitlement
// means absence of the entry.
import {
  boardOpensWith,
  isBoardKind,
  type BoardEntitledRow,
  type BoardKind,
} from "@cleo/contracts";
import { useMemo } from "react";

import { isSingletonBoardKind, type SingletonBoardKind } from "../boardDeckStore";
import { boardRegistrySnapshot, useWorldBoards } from "./boardRegistryClient";

export type Workroom = BoardEntitledRow;

export interface WorkroomMenuEntry {
  readonly workroomKey: string;
  readonly title: string;
  /** The board kind the room (or the deck tab) opens with — the row's key. */
  readonly board: BoardKind;
}

export interface LaunchableWorkroomsOutcome {
  readonly boards: ReadonlyArray<Workroom>;
  readonly loading: boolean;
  readonly refusal: { readonly kind: string; readonly reason: string } | null;
}

/**
 * The one fold (SB-CAN-09, D-SB10): exactly the rows the door folded as
 * launchable for this person in this World. A tenancy- or role-refused board
 * is simply absent — it is not this person's absence to explain.
 */
export function resolveLaunchableWorkrooms(
  boards: ReadonlyArray<Workroom>,
): ReadonlyArray<Workroom> {
  return boards.filter((board) => board.launchable);
}

/**
 * Rooms whose launchers wait for their published instructions. The World refuses a session in a built-in room whose
 * platform layer (room/<key>) is not published (backend sessionInstructions.js, INSTRUCTION_ROOM_REQUIRED), and the
 * registry row already says whether it is: a built-in room's `doctrine` is that layer's text, or null when none is
 * published (backend lib/boardOps.js projectBoardWithWording). The rooms of the 4 October copy all have theirs; a room
 * added after it is named here, so no door to it shows until it can start a session. Can 73: the Members room.
 */
export const ROOMS_OPENED_ON_PUBLISHED_INSTRUCTIONS: ReadonlySet<string> = new Set([
  "world-members",
]);

/** Whether the registry row shows the room's instructions published, for a room that waits for them. */
export function workroomInstructionsPublished(board: Workroom): boolean {
  return (
    !ROOMS_OPENED_ON_PUBLISHED_INSTRUCTIONS.has(board.key) || (board.doctrine?.trim() ?? "") !== ""
  );
}

/** A launcher entry exists only for a row the person may launch whose room can start a session. */
const launcherReady = (board: Workroom): boolean =>
  board.launchable && workroomInstructionsPublished(board);

function entryOf(board: Workroom): WorkroomMenuEntry | null {
  // A production row opens on itself; a World-authored row (Arc SKR) on the
  // builtin its implementation names. Either way the entry's board is a kind
  // this Desk renders, or the row is absent from every menu.
  const kind = boardOpensWith(board);
  return kind === null ? null : { workroomKey: board.key, title: board.title, board: kind };
}

/** A room-born session fronts its board; a plain session fronts Goals (SB-ANTI-05).
 * A World-authored room fronts the builtin its registry row opens on, read from
 * the World's registry snapshot (never a literal). */
export function deckDefaultBoardKind(
  workroomKey: string | null | undefined,
  worldKey: string | null = null,
): SingletonBoardKind {
  if (typeof workroomKey !== "string" || workroomKey.length === 0) return "goals";
  if (isBoardKind(workroomKey)) return isSingletonBoardKind(workroomKey) ? workroomKey : "goals";
  const row =
    worldKey === null
      ? undefined
      : boardRegistrySnapshot(worldKey)?.boards.find((board) => board.key === workroomKey);
  const kind = row === undefined ? null : boardOpensWith(row);
  return kind !== null && isSingletonBoardKind(kind) ? kind : "goals";
}

/**
 * W-0e42699e (#1382): the default a brand-new plain room's deck is created
 * with, among the kinds this person may open: Goals, which is founder-only.
 * Null otherwise, and the deck opens on its empty Canvas. Written once, when
 * the deck is created; a saved deck is never changed.
 */
export function permittedDeckDefaultKind(
  allowed: ReadonlySet<BoardKind>,
): SingletonBoardKind | null {
  // 7 Oct 2026 (0.0.194 walkthrough): "Every new room opens on my Inbox, not an empty Canvas." A plain room opens
  // on Goals for whoever may open it; anyone else gets the empty Canvas, which offers only the boards they may open,
  // never a board they did not choose (the Inbox before, or the World's first board).
  return allowed.has("goals") ? "goals" : null;
}

/** The deck's fixed doors — already in the + menu by their own buttons. */
const FIXED_DECK_DOORS: ReadonlySet<string> = new Set([
  "goals",
  "preview",
  "terminal",
  "files",
  "agents",
]);

/** Nested decks consume only entitled kinds the registry marks nestable. */
export function nestedSessionBoardKinds(boards: ReadonlyArray<Workroom>): ReadonlySet<BoardKind> {
  return new Set(
    boards
      .filter((board) => board.launchable && board.nestable)
      .map((board) => boardOpensWith(board))
      .filter((kind): kind is BoardKind => kind !== null),
  );
}

/** Boards that open as a tab in the active session-view's deck — derivation only. */
export function deskOpenMenuEntries(
  boards: ReadonlyArray<Workroom>,
  nested = false,
): ReadonlyArray<WorkroomMenuEntry> {
  return boards.flatMap((board) => {
    if (
      !launcherReady(board) ||
      (nested && !board.nestable) ||
      board.openableAsRoom ||
      FIXED_DECK_DOORS.has(board.key)
    )
      return [];
    if (!isBoardKind(board.key) || !isSingletonBoardKind(board.key)) return [];
    const entry = entryOf(board);
    return entry === null ? [] : [entry];
  });
}

/**
 * FD44 (register FD-CAN-44, 2026-09-05; the principal: "remove the Music
 * icon from the left room-rail"): rooms whose ONE door is a seat on the
 * context dock. They keep their registry row, their entitlement and their
 * opening board — only the launcher chip is absent, because the dock's seat
 * already knows their live state and is the door. Named here, beside the
 * fold, so the dock still derives its chips from the registry and this one
 * exception alone; the door itself is openWorldViewWorkroom.
 */
export const CONTEXT_DOCK_SEATED_ROOMS: ReadonlySet<string> = new Set(["music"]);

/** Boards that open as a WORKROOM (RM-CAN-09): the launcher chips' species. */
export function deskSessionMenuEntries(
  boards: ReadonlyArray<Workroom>,
): ReadonlyArray<WorkroomMenuEntry> {
  return boards.flatMap((board) => {
    if (!launcherReady(board) || !board.openableAsRoom) return [];
    if (CONTEXT_DOCK_SEATED_ROOMS.has(board.key)) return [];
    const entry = entryOf(board);
    return entry === null ? [] : [entry];
  });
}

/** A deck board kind's title, from the World's registry snapshot — never a literal. */
export function workroomTitleForBoard(worldKey: string | null, kind: string): string | null {
  if (worldKey === null) return null;
  return boardRegistrySnapshot(worldKey)?.boards.find((board) => board.key === kind)?.title ?? null;
}

/**
 * The entitled boards for one World, live. Refusal is data: launch seats can
 * distinguish "no entitlement" from "the authority could not answer" and
 * render the door's sentence. The state is tagged with its World so a switch
 * can never momentarily reuse another World's answer.
 */
export function useLaunchableWorkroomsOutcome(worldKey: string | null): LaunchableWorkroomsOutcome {
  const state = useWorldBoards(worldKey);
  return useMemo(() => {
    if (worldKey === null) return { boards: [], loading: false, refusal: null };
    if (state.loading) return { boards: [], loading: true, refusal: null };
    if (state.refusal !== null) return { boards: [], loading: false, refusal: state.refusal };
    return {
      boards: resolveLaunchableWorkrooms(state.registry?.boards ?? []),
      loading: false,
      refusal: null,
    };
  }, [state, worldKey]);
}

/**
 * Whether one Workroom's launcher shows in this World: the same decision as the room chips and the command menu
 * (deskSessionMenuEntries). Null while the World's registry is being read.
 */
export function useWorkroomLauncherReady(
  worldKey: string | null,
  workroomKey: string,
): boolean | null {
  const outcome = useLaunchableWorkroomsOutcome(worldKey);
  if (outcome.loading) return null;
  return deskSessionMenuEntries(outcome.boards).some((entry) => entry.workroomKey === workroomKey);
}

/**
 * Compatibility projection for non-launcher consumers that only need the
 * successful fold. Launch seats must use useLaunchableWorkroomsOutcome so
 * they never silence a refusal.
 */
export function useLaunchableWorkrooms(worldKey: string | null): ReadonlyArray<Workroom> {
  return useLaunchableWorkroomsOutcome(worldKey).boards;
}
